/**
 * Studio de modèles de prompt — écrire, éprouver, corriger.
 *
 * L'ancien éditeur montrait un « aperçu » où les variables étaient remplacées
 * par un jeu d'exemple figé côté navigateur. Il ne prouvait rien : ni que le
 * prompt réellement envoyé ressemble à ça, ni que le modèle produit un discours
 * utilisable, ni pourquoi ce discours reste vague.
 *
 * Le banc d'essai à droite corrige les trois :
 *   - il tire les données d'une VRAIE entreprise du CRM (ou d'un exemple) ;
 *   - il affiche le prompt exact que la production enverra, profil du vendeur
 *     et contrainte de longueur compris ;
 *   - il exécute le modèle et rend le texte, sa longueur et la latence.
 *
 * Rien n'est enregistré pendant un essai : on éprouve un modèle, on ne remplit
 * pas l'historique des entreprises.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  X, Play, RefreshCw, Wand2, Search, ChevronDown, AlertTriangle, Check, Copy, Building2, Package,
} from "lucide-react";
import api, { apiError } from "@/lib/crmApi";
import Modal from "@/crm/components/Modal";

const LABEL = "flex flex-col gap-[7px] text-[13px] text-[#4c5164]";
const FIELD = "border border-[#e2e5ec] rounded-[10px] px-[13px] py-[11px] text-sm outline-none focus:border-[#5b4bff] focus:shadow-[0_0_0_3px_rgba(91,75,255,.14)]";

export default function TemplateStudio({ template, meta, onClose, onSaved }) {
  const [f, setF] = useState({
    name: "", description: "", action_type: "APPEL", context: "", system_prompt: "",
    user_template: "", max_chars: 0, active: true, ...template,
  });
  const [error, setError] = useState(false);
  const [saving, setSaving] = useState(false);
  const [assistOpen, setAssistOpen] = useState(false);
  const promptRef = useRef(null);

  const contexts = meta.contexts[f.action_type] || [];
  const set = (patch) => { setF((s) => ({ ...s, ...patch })); setError(false); };

  const save = async () => {
    if (!f.name.trim() || !f.user_template.trim()) return setError(true);
    setSaving(true);
    try {
      // Enregistrer ne ferme pas l'atelier : on relit, on ajuste, on réessaie
      // l'aperçu. Un nouveau modèle prend l'identifiant que le serveur lui
      // donne, pour que l'enregistrement suivant soit une mise à jour et non
      // un doublon.
      const { data } = f.id
        ? await api.put(`/ai/templates/${f.id}`, f)
        : await api.post("/ai/templates", f);
      if (!f.id && data?.id) setF((s) => ({ ...s, id: data.id }));
      toast.success("Modèle enregistré");
      onSaved(data);
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    setSaving(false);
  };

  /** Insère une variable à la position du curseur, pas en fin de texte. */
  const insertVariable = (name) => {
    const el = promptRef.current;
    const token = `{{${name}}}`;
    if (!el) return set({ user_template: `${f.user_template || ""}${token}` });
    const { selectionStart: a, selectionEnd: b } = el;
    const next = `${f.user_template.slice(0, a)}${token}${f.user_template.slice(b)}`;
    set({ user_template: next });
    requestAnimationFrame(() => {
      el.focus();
      el.selectionStart = el.selectionEnd = a + token.length;
    });
  };

  return (
    <Modal onClose={onClose} zClassName="z-[60]" padded={false} testid="template-modal"
      label={f.id ? "Modifier le modèle" : "Nouveau modèle"}
      className="flex max-w-[1240px] flex-col overflow-hidden rounded-[18px] shadow-[0_28px_70px_rgba(10,12,24,.34)]">
      <div className="flex items-start justify-between gap-3.5 border-b border-[#eef0f4] px-6 py-4">
        <div>
          <h3 className="font-heading text-lg font-bold">{f.id ? "Modifier le modèle" : "Nouveau modèle"}</h3>
          <p className="mt-1 text-[13px] text-[#71768a]">
            Le prompt est envoyé tel quel, variables remplacées littéralement. Éprouvez-le à droite avant d'enregistrer.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button data-testid="assist-btn" onClick={() => setAssistOpen(true)}
            className="flex items-center gap-2 rounded-[10px] border border-[#b9b1ff] bg-[#f8f7ff] px-3 py-2 text-[13px] font-medium text-[#4a3ae0] hover:bg-[#f0eeff]">
            <Wand2 size={15} /> Écrire avec l'IA
          </button>
          <button onClick={onClose} className="rounded-lg p-1.5 text-[#b3b8c7] hover:bg-[#f1f2f7] hover:text-[#4c5164]">
            <X size={16} />
          </button>
        </div>
      </div>

      <div className="grid min-h-0 flex-1 grid-cols-1 gap-0 overflow-hidden min-[1100px]:grid-cols-[minmax(0,1fr)_minmax(0,1fr)]">
        {/* ÉDITEUR */}
        <div className="min-w-0 overflow-y-auto border-[#eef0f4] p-5 min-[1100px]:border-r">
          <div className="flex flex-col gap-3.5">
            <label className={LABEL}>
              <span className="font-medium">Nom du modèle</span>
              <input data-testid="tpl-name" value={f.name} onChange={(e) => set({ name: e.target.value })}
                placeholder="Premier appel — Direct" className={FIELD} />
            </label>
            <label className={LABEL}>
              <span className="font-medium">Description</span>
              <input value={f.description} onChange={(e) => set({ description: e.target.value })}
                placeholder="À quoi sert ce modèle ?" className={FIELD} />
            </label>

            <div className="grid grid-cols-[repeat(auto-fit,minmax(150px,1fr))] gap-3.5">
              <label className={LABEL}>
                <span className="font-medium">Action</span>
                {/* Changer l'action réinitialise le contexte : la liste en dépend. */}
                <select data-testid="tpl-action" value={f.action_type}
                  onChange={(e) => set({ action_type: e.target.value, context: "" })}
                  className={`${FIELD} bg-white`}>
                  {meta.actions.map((a) => <option key={a.code} value={a.code}>{a.label}</option>)}
                </select>
              </label>
              <label className={LABEL}>
                <span className="font-medium">Contexte</span>
                <select data-testid="tpl-context" value={f.context}
                  onChange={(e) => set({ context: e.target.value })} className={`${FIELD} bg-white`}>
                  <option value="">— Tous —</option>
                  {contexts.map((c) => <option key={c}>{c}</option>)}
                </select>
              </label>
              <label className={LABEL}>
                <span className="font-medium">Longueur max.</span>
                <div className="flex items-center gap-2">
                  <input data-testid="tpl-max-chars" type="number" min={0} max={4000} step={50}
                    value={f.max_chars || 0}
                    onChange={(e) => set({ max_chars: Number(e.target.value) })}
                    className={`${FIELD} w-full`} />
                  <span className="whitespace-nowrap text-[12px] text-[#8a90a3]">car.</span>
                </div>
                <span className="text-[11.5px] text-[#9aa0b2]">0 = sans limite · un script d'appel tient en 700</span>
              </label>
            </div>

            <label className={LABEL}>
              <span className="font-medium">Prompt système <span className="font-normal text-[#9aa0b2]">(optionnel)</span></span>
              <textarea rows={2} value={f.system_prompt} onChange={(e) => set({ system_prompt: e.target.value })}
                placeholder="Ton, langue, posture du modèle…" className={`${FIELD} resize-y text-[13.5px]`} />
            </label>

            <label className={LABEL}>
              <span className="flex items-center justify-between font-medium">
                Prompt (modèle)
                <span className="font-mono text-[11px] font-normal text-[#8a90a3]">
                  {(f.user_template || "").length} car.
                </span>
              </span>
              <textarea ref={promptRef} data-testid="tpl-user" rows={9} value={f.user_template}
                onChange={(e) => set({ user_template: e.target.value })}
                placeholder="Ex : Rédige un script d'appel pour {{entreprise.nom}}…"
                className={`${FIELD} resize-y font-mono text-[13px] leading-[1.65]`} />
            </label>

            <div className="rounded-[14px] border border-[#eceef4] bg-[#f7f8fa] p-3.5">
              <div className="font-mono text-[10.5px] uppercase tracking-[.12em] text-[#8a90a3]">Variables</div>
              <p className="mb-2.5 mt-2 text-[12.5px] text-[#71768a]">
                Cliquez pour insérer à l'endroit du curseur.
              </p>
              <div className="flex max-h-[168px] flex-wrap gap-1.5 overflow-y-auto">
                {meta.variables.map((v) => (
                  <button key={v} onClick={() => insertVariable(v)}
                    className={`rounded-[7px] border px-[7px] py-1 font-mono text-[11px] hover:border-[#b9b1ff] hover:bg-[#f4f2ff] hover:text-[#3a2cc8] ${v.startsWith("nous.") ? "border-[#cfe3f7] bg-[#f2f8fd] text-[#1f5f9e]" : "border-[#e6e8ee] bg-white text-[#5b6072]"}`}>
                    {`{{${v}}}`}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-center justify-between gap-3.5 border-t border-[#f0f1f5] pt-4">
              <button onClick={() => set({ active: !f.active })} className="flex items-center gap-2.5 text-[13.5px] text-[#4c5164]">
                <span className={`flex h-[26px] w-[46px] rounded-full p-[3px] ${f.active ? "justify-end bg-[#4a3ae0]" : "justify-start bg-[#d3d7e1]"}`}>
                  <span className="h-5 w-5 rounded-full bg-white shadow-[0_1px_3px_rgba(0,0,0,.2)]" />
                </span>
                <span>{f.active ? "Modèle actif" : "Modèle désactivé"}</span>
              </button>
              <div className="flex gap-2.5">
                <button onClick={onClose}
                  className="rounded-[11px] border border-[#e2e5ec] bg-white px-4 py-[11px] text-sm text-[#4c5164] hover:border-[#c9cedb]">
                  Annuler
                </button>
                <button data-testid="tpl-save" onClick={save} disabled={saving}
                  className="rounded-[11px] bg-[#4a3ae0] px-[18px] py-[11px] text-sm font-medium text-white shadow-[0_6px_18px_rgba(74,58,224,.24)] hover:bg-[#3a2cc8] disabled:opacity-60">
                  {saving ? "Enregistrement…" : "Enregistrer"}
                </button>
              </div>
            </div>
            {error && (
              <div className="rounded-[10px] border border-[#f6d5d1] bg-[#fdeceb] px-3 py-2.5 text-[13px] text-[#d64a3a]">
                Nom et prompt requis.
              </div>
            )}
          </div>
        </div>

        {/* BANC D'ESSAI */}
        <TestBench template={f} />
      </div>

      {assistOpen && (
        <AssistPanel current={f} onClose={() => setAssistOpen(false)}
          onApply={(proposal) => { set(proposal); setAssistOpen(false); toast.success("Proposition appliquée — relisez avant d'enregistrer"); }} />
      )}
    </Modal>
  );
}

/** Éprouve le modèle sur une vraie entreprise, sans rien enregistrer. */
function TestBench({ template }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [company, setCompany] = useState(null);
  const [useSite, setUseSite] = useState(false);
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const [showPrompt, setShowPrompt] = useState(false);
  const [copied, setCopied] = useState(false);

  // Recherche différée : on ne frappe pas l'API à chaque touche.
  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setResults([]); return undefined; }
    const timer = setTimeout(() => {
      api.get("/companies", { params: { search: q, limit: 6 } })
        .then(({ data }) => setResults(data.items || []))
        .catch(() => setResults([]));
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  const run = useCallback(async (withModel) => {
    setRunning(true);
    try {
      const { data } = await api.post("/ai/templates/preview", {
        template, company_id: company?.id, use_site_analysis: useSite,
        max_chars: template.max_chars, run: withModel,
      });
      setResult(data);
      if (!withModel) setShowPrompt(true);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Essai impossible");
    } finally { setRunning(false); }
  }, [template, company, useSite]);

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(result?.text || "");
      setCopied(true); setTimeout(() => setCopied(false), 1500);
    } catch { toast.error("Copie impossible"); }
  };

  const over = result?.max_chars > 0 && result?.chars > result.max_chars;

  return (
    <div className="min-w-0 overflow-y-auto bg-[#fbfbfd] p-5" data-testid="template-testbench">
      <div className="mb-3 flex items-center justify-between gap-2">
        <span className="font-mono text-[10.5px] uppercase tracking-[.12em] text-[#8a90a3]">Banc d'essai</span>
        <span className="text-[11.5px] text-[#9aa0b2]">Aucun enregistrement</span>
      </div>

      {/* CIBLE DE L'ESSAI */}
      <div className="rounded-[14px] border border-[#e6e8ee] bg-white p-3.5">
        <div className="flex items-center gap-2 rounded-[10px] border border-[#e2e5ec] px-3 py-2 focus-within:border-[#5b4bff]">
          <Search size={15} className="shrink-0 text-[#9aa0b2]" />
          <input data-testid="bench-search" value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Tester sur une vraie entreprise…"
            className="w-full border-0 bg-transparent text-[13.5px] outline-none" />
          {company && (
            <button onClick={() => { setCompany(null); setQuery(""); }} title="Revenir à l'exemple"
              className="text-[#b3b8c7] hover:text-[#4c5164]"><X size={14} /></button>
          )}
        </div>

        {results.length > 0 && !company && (
          <div className="mt-1.5 max-h-[150px] overflow-y-auto rounded-[10px] border border-[#eceef4]">
            {results.map((c) => (
              <button key={c.id} onClick={() => { setCompany(c); setResults([]); setQuery(c.nom); }}
                className="block w-full px-3 py-2 text-left text-[13px] hover:bg-[#f7f8fa]">
                <span className="text-[#16181d]">{c.nom}</span>
                <span className="ml-2 text-[11.5px] text-[#8a90a3]">{c.ville || "—"} · {c.status}</span>
              </button>
            ))}
          </div>
        )}

        <div className="mt-2.5 flex flex-wrap items-center gap-3">
          <span className="flex items-center gap-1.5 text-[12.5px] text-[#61667a]">
            <Building2 size={13} className="text-[#8a90a3]" />
            {company ? company.nom : "Boulangerie Martin (exemple)"}
          </span>
          {/* La solution que le moteur a retenue pour cette entreprise : c'est
              elle qui nourrit {{nous.*}} et le contexte du vendeur. */}
          {result?.solution && (
            <span data-testid="bench-solution" className="flex items-center gap-1.5 text-[12.5px] text-[#1f5f9e]">
              <Package size={13} /> {result.solution.nom}
            </span>
          )}
          <label className="flex cursor-pointer items-center gap-1.5 text-[12.5px] text-[#61667a]">
            <input type="checkbox" checked={useSite} onChange={(e) => setUseSite(e.target.checked)} />
            Analyse du site
          </label>
        </div>

        <div className="mt-3 flex gap-2">
          <button data-testid="bench-run" onClick={() => run(true)} disabled={running}
            className="flex flex-1 items-center justify-center gap-2 rounded-[10px] bg-[#16181d] py-2.5 text-[13.5px] font-medium text-white hover:bg-[#2d3142] disabled:opacity-50">
            {running ? <RefreshCw size={14} className="animate-spin" /> : <Play size={14} />}
            Générer pour de vrai
          </button>
          <button onClick={() => run(false)} disabled={running}
            className="rounded-[10px] border border-[#e2e5ec] px-3 py-2.5 text-[13px] text-[#4c5164] hover:border-[#c9cedb] disabled:opacity-50">
            Voir le prompt
          </button>
        </div>
      </div>

      {/* DIAGNOSTICS */}
      {result?.profile_empty && (
        <div data-testid="bench-profile-empty" className="mt-3 flex items-start gap-2 rounded-[12px] border border-[#f6e2b8] bg-[#fdf3dd] px-3 py-2.5 text-[12.5px] text-[#8a6b1f]">
          <AlertTriangle size={14} className="mt-0.5 shrink-0" />
          <span>
            Ni identité du vendeur ni solution adaptée à cette entreprise : le discours ne saura
            ni qui appelle ni ce qui est proposé.
            <Link to="/solutions" className="ml-1 font-medium underline hover:no-underline">
              Renseigner les solutions
            </Link>
          </span>
        </div>
      )}
      {result?.missing_variables?.length > 0 && (
        <div data-testid="bench-missing" className="mt-3 rounded-[12px] border border-[#f6e2b8] bg-[#fdf3dd] px-3 py-2.5 text-[12.5px] text-[#8a6b1f]">
          Variables sans valeur pour cette entreprise — elles rendront du vide :
          <span className="ml-1 font-mono">{result.missing_variables.join(", ")}</span>
        </div>
      )}

      {/* RÉSULTAT */}
      {result?.text != null && (
        <div className="mt-3 rounded-[14px] border border-[#e6e8ee] bg-white">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-[#eef0f4] px-3.5 py-2.5">
            <span className="text-[12.5px] font-medium text-[#4c5164]">Discours produit</span>
            <span className="flex items-center gap-2.5 font-mono text-[11px] text-[#8a90a3]">
              <span className={over ? "text-[#d64a3a]" : ""}>
                {result.chars}{result.max_chars ? ` / ${result.max_chars}` : ""} car.
              </span>
              {result.latency_ms != null && <span>{(result.latency_ms / 1000).toFixed(1)} s</span>}
              {result.model && <span className="hidden sm:inline">{result.model}</span>}
            </span>
          </div>
          <div data-testid="bench-output" className="whitespace-pre-wrap px-3.5 py-3 text-[13.5px] leading-relaxed text-[#16181d]">
            {result.text}
          </div>
          <div className="flex items-center gap-2 border-t border-[#eef0f4] px-3.5 py-2.5">
            <button onClick={copy}
              className="flex items-center gap-1.5 rounded-lg border border-[#e2e5ec] px-2.5 py-1.5 text-[12px] text-[#4c5164] hover:border-[#c9cedb]">
              {copied ? <Check size={12} className="text-[#2f9e5c]" /> : <Copy size={12} />} {copied ? "Copié" : "Copier"}
            </button>
            <button onClick={() => run(true)} disabled={running}
              className="flex items-center gap-1.5 rounded-lg border border-[#e2e5ec] px-2.5 py-1.5 text-[12px] text-[#4c5164] hover:border-[#c9cedb] disabled:opacity-50">
              <RefreshCw size={12} /> Relancer
            </button>
            {result.truncated && (
              <span className="text-[11.5px] text-[#8a6b1f]">
                Coupé à la limite ({result.raw_chars} car. produits)
              </span>
            )}
          </div>
        </div>
      )}

      {/* PROMPT RÉELLEMENT ENVOYÉ */}
      {result && (
        <div className="mt-3 rounded-[14px] border border-[#e6e8ee] bg-white">
          <button onClick={() => setShowPrompt((v) => !v)}
            className="flex w-full items-center justify-between px-3.5 py-2.5 text-[12.5px] font-medium text-[#4c5164]">
            Prompt envoyé au modèle
            <span className="flex items-center gap-2 font-mono text-[11px] font-normal text-[#8a90a3]">
              {result.prompt_chars} car.
              <ChevronDown size={14} className={showPrompt ? "rotate-180 transition-transform" : "transition-transform"} />
            </span>
          </button>
          {showPrompt && (
            <div className="border-t border-[#eef0f4] px-3.5 py-3">
              {result.system_prompt_used && (
                <>
                  <div className="font-mono text-[10.5px] uppercase tracking-[.1em] text-[#9aa0b2]">Système</div>
                  <pre className="mb-3 mt-1 whitespace-pre-wrap font-mono text-[11.5px] leading-[1.6] text-[#5b6072]">
                    {result.system_prompt_used}
                  </pre>
                </>
              )}
              <div className="font-mono text-[10.5px] uppercase tracking-[.1em] text-[#9aa0b2]">Utilisateur</div>
              <pre data-testid="bench-prompt" className="mt-1 whitespace-pre-wrap font-mono text-[11.5px] leading-[1.6] text-[#3a3f52]">
                {result.prompt_used}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** L'IA propose un modèle ; l'équipe le relit et l'enregistre. */
function AssistPanel({ current, onClose, onApply }) {
  const [instruction, setInstruction] = useState("");
  const [loading, setLoading] = useState(false);
  const [proposal, setProposal] = useState(null);

  const ask = async () => {
    if (!instruction.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.post("/ai/templates/assist", {
        instruction, action_type: current.action_type, context: current.context,
        current: { name: current.name, user_template: current.user_template },
      });
      setProposal(data);
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail) || "Assistant indisponible");
    } finally { setLoading(false); }
  };

  return (
    <div className="absolute inset-0 z-10 flex items-center justify-center bg-[rgba(12,14,22,.45)] p-5" onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} data-testid="assist-panel"
        className="max-h-[86vh] w-full max-w-[640px] overflow-y-auto rounded-[16px] bg-white p-5">
        <div className="flex items-start justify-between">
          <div>
            <h4 className="font-heading text-[16px] font-bold">Écrire le modèle avec l'IA</h4>
            <p className="mt-1 text-[13px] text-[#71768a]">
              Décrivez l'intention ; l'IA rédige le prompt avec les bonnes variables. Vous relisez avant d'enregistrer.
            </p>
          </div>
          <button onClick={onClose} className="rounded-lg p-1 text-[#b3b8c7] hover:bg-[#f1f2f7]"><X size={16} /></button>
        </div>

        <textarea data-testid="assist-instruction" rows={3} value={instruction}
          onChange={(e) => setInstruction(e.target.value)}
          placeholder="Ex : un script d'appel pour artisans, très court, qui parle du temps perdu en devis et propose un rendez-vous."
          className={`${FIELD} mt-4 w-full resize-y text-[13.5px]`} />

        <button data-testid="assist-run" onClick={ask} disabled={loading || !instruction.trim()}
          className="mt-2.5 flex w-full items-center justify-center gap-2 rounded-[10px] bg-[#4a3ae0] py-2.5 text-[13.5px] font-medium text-white hover:bg-[#3a2cc8] disabled:opacity-50">
          {loading ? <RefreshCw size={14} className="animate-spin" /> : <Wand2 size={14} />}
          {proposal ? "Proposer autre chose" : "Proposer un modèle"}
        </button>

        {proposal && (
          <div className="mt-4 rounded-[12px] border border-[#e6e8ee]">
            <div className="border-b border-[#eef0f4] px-3.5 py-2.5 text-[13px] font-medium">
              {proposal.name || "Proposition"}
              {proposal.max_chars ? (
                <span className="ml-2 font-mono text-[11px] text-[#8a90a3]">{proposal.max_chars} car. max</span>
              ) : null}
            </div>
            {proposal.description && (
              <p className="px-3.5 pt-2.5 text-[12.5px] text-[#71768a]">{proposal.description}</p>
            )}
            <pre className="whitespace-pre-wrap px-3.5 py-3 font-mono text-[12px] leading-[1.65] text-[#3a3f52]">
              {proposal.user_template}
            </pre>
            <div className="flex gap-2 border-t border-[#eef0f4] px-3.5 py-2.5">
              <button data-testid="assist-apply"
                onClick={() => onApply({
                  ...(proposal.name ? { name: proposal.name } : {}),
                  ...(proposal.description ? { description: proposal.description } : {}),
                  ...(proposal.system_prompt ? { system_prompt: proposal.system_prompt } : {}),
                  ...(proposal.max_chars ? { max_chars: proposal.max_chars } : {}),
                  user_template: proposal.user_template,
                })}
                className="rounded-[10px] bg-[#16181d] px-3.5 py-2 text-[12.5px] font-medium text-white hover:bg-[#2d3142]">
                Appliquer dans l'éditeur
              </button>
              <button onClick={onClose}
                className="rounded-[10px] border border-[#e2e5ec] px-3.5 py-2 text-[12.5px] text-[#4c5164] hover:border-[#c9cedb]">
                Ignorer
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
